import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  RotateCcw,
  Upload,
  XCircle,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import {
  BulkUploadField,
  ColumnMapping,
  MappedRow,
  SpreadsheetData,
  buildDefaultIncluded,
  buildDefaultMapping,
  downloadExcelTemplate,
  loadSavedBulkConfig,
  mapSpreadsheetRows,
  matchSpreadsheetHeaders,
  parseSpreadsheet,
  saveBulkConfig,
  validateMapping,
} from "../../lib/bulkUpload";
import AppButton from "./AppButton";
import { Spinner } from "./common";
import { Modal } from "./Modal";

type Step = 1 | 2 | 3;

interface UploadLogItem {
  rowNumber: number;
  label: string;
  status: "success" | "error";
  message?: string;
}

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  entityLabel: string;
  entitySingular?: string;
  fields: BulkUploadField[];
  storageKey: string;
  templateFileName: string;
  onUploadRow: (row: Record<string, unknown>) => Promise<void>;
  onUploadComplete?: (result: { succeeded: number; failed: number }) => void;
  getRowLabel?: (row: Record<string, unknown>) => string;
  validateRecord?: (record: Record<string, unknown>) => string[];
  transformRecord?: (
    record: Record<string, unknown>,
  ) => Record<string, unknown>;
}

const STEPS: { id: Step; label: string }[] = [
  { id: 1, label: "Column names" },
  { id: 2, label: "Upload file" },
  { id: 3, label: "Review" },
];

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  onClose,
  title,
  entityLabel,
  entitySingular,
  fields,
  storageKey,
  templateFileName,
  onUploadRow,
  onUploadComplete,
  getRowLabel,
  validateRecord,
  transformRecord,
}) => {
  const singularLabel = entitySingular || entityLabel.replace(/s$/, "");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const logEndRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState<Step>(1);
  const [mapping, setMapping] = useState<ColumnMapping>(() =>
    buildDefaultMapping(fields),
  );
  const [includedKeys, setIncludedKeys] = useState<Record<string, boolean>>(
    () => buildDefaultIncluded(fields),
  );
  const [spreadsheet, setSpreadsheet] = useState<SpreadsheetData | null>(null);
  const [fileError, setFileError] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [skipInvalidRows, setSkipInvalidRows] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFinished, setUploadFinished] = useState(false);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadCurrent, setUploadCurrent] = useState(0);
  const [uploadSucceeded, setUploadSucceeded] = useState(0);
  const [currentLabel, setCurrentLabel] = useState("");
  const [uploadLog, setUploadLog] = useState<UploadLogItem[]>([]);

  const resetUploadState = useCallback(() => {
    setIsUploading(false);
    setUploadFinished(false);
    setUploadTotal(0);
    setUploadCurrent(0);
    setUploadSucceeded(0);
    setCurrentLabel("");
    setUploadLog([]);
  }, []);

  const resetTransientState = useCallback(() => {
    setStep(1);
    setSpreadsheet(null);
    setFileError("");
    setIsParsing(false);
    setIsDragging(false);
    setSkipInvalidRows(true);
    resetUploadState();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [resetUploadState]);

  useEffect(() => {
    if (!isOpen) {
      resetTransientState();
      return;
    }

    const saved = loadSavedBulkConfig(storageKey);
    const defaults = buildDefaultMapping(fields);
    const defaultIncluded = buildDefaultIncluded(fields);

    if (saved) {
      const nextMapping = { ...defaults };
      fields.forEach((field) => {
        if (saved.mapping[field.key]) {
          nextMapping[field.key] = saved.mapping[field.key];
        }
      });
      const nextIncluded = { ...defaultIncluded };
      fields.forEach((field) => {
        if (typeof saved.includedKeys[field.key] === "boolean") {
          nextIncluded[field.key] = saved.includedKeys[field.key];
        }
      });
      setMapping(nextMapping);
      setIncludedKeys(nextIncluded);
    } else {
      setMapping(defaults);
      setIncludedKeys(defaultIncluded);
    }
    // Field config is stable per entity; re-hydrate only when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, storageKey, resetTransientState]);

  const mappingErrors = useMemo(
    () => validateMapping(fields, mapping, includedKeys),
    [fields, mapping, includedKeys],
  );

  const headerMatch = useMemo(() => {
    if (!spreadsheet) return null;
    return matchSpreadsheetHeaders(
      spreadsheet.headers,
      fields,
      mapping,
      includedKeys,
    );
  }, [spreadsheet, fields, mapping, includedKeys]);

  const mappedRows = useMemo<MappedRow[]>(() => {
    if (!spreadsheet || (headerMatch && headerMatch.missing.length > 0)) {
      return [];
    }
    return mapSpreadsheetRows(
      spreadsheet,
      fields,
      mapping,
      includedKeys,
      validateRecord,
      transformRecord,
    );
  }, [
    spreadsheet,
    fields,
    mapping,
    includedKeys,
    validateRecord,
    transformRecord,
    headerMatch,
  ]);

  const validRows = useMemo(
    () => mappedRows.filter((row) => row.errors.length === 0),
    [mappedRows],
  );
  const invalidRows = useMemo(
    () => mappedRows.filter((row) => row.errors.length > 0),
    [mappedRows],
  );

  const handleClose = () => {
    if (isUploading) return;
    saveBulkConfig(storageKey, mapping, includedKeys);
    onClose();
  };

  const updateColumnName = (key: string, value: string) => {
    setMapping((prev) => ({ ...prev, [key]: value }));
  };

  const toggleIncluded = (key: string, included: boolean) => {
    setIncludedKeys((prev) => ({ ...prev, [key]: included }));
  };

  const resetMappings = () => {
    setMapping(buildDefaultMapping(fields));
    setIncludedKeys(buildDefaultIncluded(fields));
  };

  const handleDownloadTemplate = () => {
    if (mappingErrors.length > 0) return;
    const headers = fields
      .filter((field) => field.required || includedKeys[field.key])
      .map((field) => mapping[field.key].trim());
    downloadExcelTemplate(headers, templateFileName, entityLabel);
  };

  const processFile = async (file: File | null) => {
    if (!file) return;
    setFileError("");
    setIsParsing(true);
    try {
      const parsed = await parseSpreadsheet(file);
      setSpreadsheet(parsed);
    } catch (error) {
      setSpreadsheet(null);
      setFileError(
        error instanceof Error
          ? error.message
          : "Could not read this spreadsheet.",
      );
    } finally {
      setIsParsing(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    void processFile(file || null);
  };

  const goNext = () => {
    saveBulkConfig(storageKey, mapping, includedKeys);
    if (step === 1 && mappingErrors.length === 0) {
      setStep(2);
      return;
    }
    if (step === 2 && spreadsheet && headerMatch?.missing.length === 0) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    const rowsToUpload = skipInvalidRows ? validRows : mappedRows;
    if (
      rowsToUpload.length === 0 ||
      (!skipInvalidRows && invalidRows.length > 0)
    ) {
      return;
    }

    setIsUploading(true);
    setUploadFinished(false);
    setUploadTotal(rowsToUpload.length);
    setUploadCurrent(0);
    setUploadSucceeded(0);
    setCurrentLabel("");
    setUploadLog([]);

    let succeeded = 0;

    for (let index = 0; index < rowsToUpload.length; index += 1) {
      const row = rowsToUpload[index];
      const label =
        getRowLabel?.(row.data) ||
        String(row.data.vehicleName || `Row ${row.rowNumber}`);
      setUploadCurrent(index + 1);
      setCurrentLabel(label);

      try {
        await onUploadRow(row.data);
        succeeded += 1;
        setUploadSucceeded(succeeded);
        setUploadLog((prev) => [
          ...prev,
          { rowNumber: row.rowNumber, label, status: "success" },
        ]);
      } catch (error) {
        setUploadLog((prev) => [
          ...prev,
          {
            rowNumber: row.rowNumber,
            label,
            status: "error",
            message:
              error instanceof Error
                ? error.message
                : "Failed to upload this row.",
          },
        ]);
      }
    }

    const failed = rowsToUpload.length - succeeded;
    setIsUploading(false);
    setUploadFinished(true);
    setCurrentLabel("");

    if (failed === 0) {
      await new Promise((resolve) => setTimeout(resolve, 700));
      onUploadComplete?.({ succeeded, failed });
    }
  };

  const previewRows = mappedRows.slice(0, 50);
  const activeColumnCount = fields.filter(
    (field) => field.required || includedKeys[field.key],
  ).length;
  const failedCount = uploadLog.filter((item) => item.status === "error").length;
  const completedCount = uploadLog.length;
  const uploadPercentage =
    uploadTotal === 0
      ? 0
      : Math.round((completedCount / uploadTotal) * 100);
  const showUploadProgress = isUploading || uploadFinished;

  useEffect(() => {
    if (!showUploadProgress) return;
    logEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [uploadLog, showUploadProgress]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      className="max-w-[760px] w-full bg-white px-2 pb-8 overflow-hidden"
    >
      <div className="px-6 pt-4 pb-2">
        <p className="text-sm text-[#6B6B6B] mb-5">
          {step === 1 &&
            `Your spreadsheet can use its own column names. Map each ${singularLabel} field to the header in your file, then attach the sheet.`}
          {step === 2 &&
            "Attach the Excel or CSV file that uses the column names you just set."}
          {step === 3 &&
            !showUploadProgress &&
            `Confirm the mapped rows before uploading ${entityLabel}.`}
          {step === 3 &&
            isUploading &&
            `Uploading ${entityLabel} one at a time. Keep this window open until it finishes.`}
          {step === 3 &&
            uploadFinished &&
            (failedCount > 0
              ? "Some rows could not be uploaded. Review the results below."
              : `All ${entityLabel} were uploaded.`)}
        </p>

        {!showUploadProgress && (
          <ol className="flex items-center gap-2 mb-2">
            {STEPS.map((item, index) => {
              const isCurrent = step === item.id;
              const isDone = step > item.id;
              return (
                <li key={item.id} className="flex items-center flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={cn(
                        "flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold shrink-0",
                        isCurrent && "bg-primary text-white",
                        isDone && "bg-primary/15 text-primary",
                        !isCurrent && !isDone && "bg-[#F1F1F1] text-[#8A8A8A]",
                      )}
                    >
                      {isDone ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        item.id
                      )}
                    </span>
                    <span
                      className={cn(
                        "text-[13px] truncate",
                        isCurrent
                          ? "text-hColor font-semibold"
                          : "text-[#8A8A8A]",
                      )}
                    >
                      {item.label}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={cn(
                        "flex-1 h-px mx-3",
                        isDone ? "bg-primary/40" : "bg-[#E8E8E8]",
                      )}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="px-6 pb-4">
        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[13px] text-[#6B6B6B]">
                Required fields must have a column name. Optional fields can be turned off if they are not in your sheet.
              </p>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  disabled={mappingErrors.length > 0}
                  className="inline-flex items-center text-[12px] text-primary hover:text-primary/80 disabled:opacity-40"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Download template
                </button>
                <button
                  type="button"
                  onClick={resetMappings}
                  className="inline-flex items-center text-[12px] text-[#6B6B6B] hover:text-hColor"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  Reset
                </button>
              </div>
            </div>

            <div className="border border-[#F1F1F1] rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1.1fr,1.4fr] gap-4 bg-[#FAFAFA] px-4 py-2.5 text-[12px] font-semibold text-[#6B6B6B]">
                <span>System field</span>
                <span>Column name in your sheet</span>
              </div>
              <div className="max-h-[42vh] overflow-y-auto divide-y divide-[#F6F6F6]">
                {fields.map((field) => {
                  const included = field.required || includedKeys[field.key];
                  return (
                    <div
                      key={field.key}
                      className="grid grid-cols-[1.1fr,1.4fr] gap-4 px-4 py-3 items-start"
                    >
                      <div className="pt-2">
                        <div className="flex items-start gap-2">
                          {!field.required ? (
                            <input
                              type="checkbox"
                              checked={included}
                              onChange={(event) =>
                                toggleIncluded(field.key, event.target.checked)
                              }
                              className="mt-1 accent-primary shrink-0"
                              aria-label={`Include ${field.label}`}
                            />
                          ) : (
                            <span className="w-4 shrink-0" aria-hidden />
                          )}
                          <div>
                            <p className="text-[13px] font-semibold text-hColor">
                              {field.label}
                              {field.required && (
                                <span className="text-primary ml-0.5">*</span>
                              )}
                            </p>
                            {(field.hint || field.example) && (
                              <p className="text-[11px] text-[#8A8A8A] mt-0.5">
                                {field.hint}
                                {field.example ? ` e.g. ${field.example}` : ""}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <input
                        type="text"
                        value={mapping[field.key] ?? ""}
                        onChange={(event) =>
                          updateColumnName(field.key, event.target.value)
                        }
                        disabled={!included}
                        placeholder={field.defaultColumnName}
                        className={cn(
                          "w-full bg-[#F6F6F6] border border-[#F6F6F6] h-[44px] px-4 rounded-lg text-[13px] outline-none transition-all",
                          "focus:ring-2 focus:ring-primary focus:border-transparent",
                          !included && "opacity-50 cursor-not-allowed",
                        )}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {mappingErrors.length > 0 && (
              <div className="flex items-start gap-2 text-[13px] text-primary bg-primary/5 rounded-md px-3 py-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{mappingErrors[0]}</span>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              className="hidden"
              onChange={(event) => {
                void processFile(event.target.files?.[0] || null);
                event.target.value = "";
              }}
            />

            <div
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "border-2 border-dashed rounded-xl px-6 py-10 text-center cursor-pointer transition-colors",
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-[#D9D9D9] bg-[#FAFAFA] hover:border-primary/50",
              )}
            >
              {isParsing ? (
                <div className="flex flex-col items-center text-[#6B6B6B]">
                  <Spinner />
                  <p className="mt-2 text-sm">Reading spreadsheet…</p>
                </div>
              ) : spreadsheet ? (
                <div className="flex flex-col items-center">
                  <FileSpreadsheet className="w-10 h-10 text-primary mb-3" />
                  <p className="text-[15px] font-semibold text-hColor">
                    {spreadsheet.fileName}
                  </p>
                  <p className="text-[13px] text-[#6B6B6B] mt-1">
                    {spreadsheet.rows.length} data{" "}
                    {spreadsheet.rows.length === 1 ? "row" : "rows"} ·{" "}
                    {spreadsheet.headers.length} columns
                  </p>
                  <p className="text-[12px] text-primary mt-3">
                    Click to replace file
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                    <Upload className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-[15px] font-semibold text-hColor">
                    Drop your spreadsheet here
                  </p>
                  <p className="text-[13px] text-[#6B6B6B] mt-1">
                    or click to browse · .xlsx, .xls, .csv · max 10MB
                  </p>
                </div>
              )}
            </div>

            {fileError && (
              <div className="flex items-start gap-2 text-[13px] text-primary bg-primary/5 rounded-md px-3 py-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{fileError}</span>
              </div>
            )}

            {spreadsheet && headerMatch && (
              <div className="space-y-3">
                {headerMatch.missing.length === 0 ? (
                  <div className="flex items-start gap-2 text-[13px] text-[#059348] bg-[#059348]/5 rounded-md px-3 py-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      All {activeColumnCount} mapped columns were found. You can
                      review the rows next.
                    </span>
                  </div>
                ) : (
                  <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 space-y-2">
                    <div className="flex items-start gap-2 text-[13px] text-primary">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>
                        These column names were not found in your file. Go back
                        and match them to your headers.
                      </span>
                    </div>
                    <ul className="pl-6 text-[12px] text-[#6B6B6B] space-y-1">
                      {headerMatch.missing.map(({ field, columnName }) => (
                        <li key={field.key}>
                          <span className="font-semibold text-hColor">
                            {field.label}
                          </span>
                          {" → "}
                          “{columnName || "not set"}”
                        </li>
                      ))}
                    </ul>
                    {spreadsheet.headers.length > 0 && (
                      <p className="text-[12px] text-[#8A8A8A] pl-6">
                        Headers in file: {spreadsheet.headers.join(", ")}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {step === 3 && showUploadProgress && (
          <div className="space-y-5 py-2">
            <div className="text-center">
              <p className="text-[40px] leading-none font-semibold text-hColor tabular-nums">
                {uploadPercentage}%
              </p>
              <p className="text-[13px] text-[#6B6B6B] mt-3">
                {isUploading
                  ? `Uploading ${uploadCurrent} of ${uploadTotal}`
                  : `Finished · ${uploadSucceeded} uploaded${
                      failedCount ? `, ${failedCount} failed` : ""
                    }`}
              </p>
            </div>

            <div className="h-2.5 bg-[#F1F1F1] rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-300 ease-out",
                  failedCount > 0 && uploadFinished ? "bg-primary" : "bg-[#059348]",
                  isUploading && "bg-primary",
                )}
                style={{ width: `${uploadPercentage}%` }}
              />
            </div>

            {isUploading && (
              <div className="flex items-center justify-center gap-2 text-[13px] text-hColor">
                <Spinner />
                <span className="truncate max-w-[420px]">
                  {currentLabel || `Row ${uploadCurrent}`}
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg bg-[#059348]/10 px-3 py-3 text-center">
                <p className="text-[11px] text-[#059348]">Uploaded</p>
                <p className="text-[18px] font-semibold text-[#059348] tabular-nums">
                  {uploadSucceeded}
                </p>
              </div>
              <div className="rounded-lg bg-primary/10 px-3 py-3 text-center">
                <p className="text-[11px] text-primary">Failed</p>
                <p className="text-[18px] font-semibold text-primary tabular-nums">
                  {failedCount}
                </p>
              </div>
            </div>

            <div className="border border-[#F1F1F1] rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-[#FAFAFA] text-[12px] font-semibold text-[#6B6B6B]">
                Upload log
              </div>
              <div className="max-h-[220px] overflow-y-auto divide-y divide-[#F6F6F6]">
                {uploadLog.length === 0 && (
                  <p className="px-3 py-3 text-[12px] text-[#8A8A8A]">
                    Waiting to start...
                  </p>
                )}
                {uploadLog.map((item, index) => (
                  <div
                    key={`${item.rowNumber}-${index}`}
                    className="flex items-start gap-2 px-3 py-2 text-[12px]"
                  >
                    {item.status === "success" ? (
                      <CheckCircle2 className="w-4 h-4 text-[#059348] mt-0.5 shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-hColor truncate">
                        Row {item.rowNumber}
                        {item.label ? ` · ${item.label}` : ""}
                      </p>
                      {item.message && (
                        <p className="text-primary mt-0.5">{item.message}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={logEndRef} />
              </div>
            </div>
          </div>
        )}

        {step === 3 && !showUploadProgress && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg bg-[#F6F6F6] px-3 py-3">
                <p className="text-[11px] text-[#8A8A8A]">Rows found</p>
                <p className="text-[18px] font-semibold text-hColor">
                  {mappedRows.length}
                </p>
              </div>
              <div className="rounded-lg bg-[#059348]/10 px-3 py-3">
                <p className="text-[11px] text-[#059348]">Ready to upload</p>
                <p className="text-[18px] font-semibold text-[#059348]">
                  {validRows.length}
                </p>
              </div>
              <div className="rounded-lg bg-red-50 px-3 py-3">
                <p className="text-[11px] text-red-600">Rows with errors</p>
                <p className="text-[18px] font-semibold text-red-600">
                  {invalidRows.length}
                </p>
              </div>
            </div>

            {invalidRows.length > 0 && (
              <label className="flex items-center gap-2 text-[13px] text-hColor">
                <input
                  type="checkbox"
                  checked={skipInvalidRows}
                  onChange={(event) => setSkipInvalidRows(event.target.checked)}
                  className="accent-primary"
                />
                Skip rows with errors and upload the {validRows.length} valid{" "}
                {validRows.length === 1 ? "row" : "rows"}
              </label>
            )}

            <div className="border border-[#F1F1F1] rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[36vh]">
                <table className="min-w-full text-[12px]">
                  <thead className="bg-primary/20 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-hColor">
                        Row
                      </th>
                      {fields
                        .filter(
                          (field) => field.required || includedKeys[field.key],
                        )
                        .map((field) => (
                          <th
                            key={field.key}
                            className="px-3 py-2 text-left font-semibold text-hColor whitespace-nowrap"
                          >
                            {field.label}
                          </th>
                        ))}
                      <th className="px-3 py-2 text-left font-semibold text-hColor">
                        Issues
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F1F1]">
                    {previewRows.map((row) => (
                      <tr
                        key={row.rowNumber}
                        className={row.errors.length ? "bg-primary/5" : "bg-white"}
                      >
                        <td className="px-3 py-2 text-[#6B6B6B]">
                          {row.rowNumber}
                        </td>
                        {fields
                          .filter(
                            (field) =>
                              field.required || includedKeys[field.key],
                          )
                          .map((field) => (
                            <td
                              key={field.key}
                              className="px-3 py-2 text-hColor whitespace-nowrap max-w-[160px] truncate"
                            >
                              {row.data[field.key] == null
                                ? "—"
                                : String(row.data[field.key])}
                            </td>
                          ))}
                        <td className="px-3 py-2 text-primary min-w-[180px]">
                          {row.errors.length ? row.errors.join(" · ") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {mappedRows.length > 50 && (
                <p className="px-3 py-2 text-[12px] text-[#8A8A8A] bg-[#FAFAFA]">
                  Showing the first 50 of {mappedRows.length} rows.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-center items-center gap-3 text-[15px] mt-2 px-6">
        {showUploadProgress ? (
          uploadFinished ? (
            <AppButton
              type="button"
              onClick={handleClose}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors text-[15px]"
            >
              Done
            </AppButton>
          ) : (
            <p className="text-[13px] text-[#8A8A8A]">
              Uploading… {uploadPercentage}%
            </p>
          )
        ) : (
          <>
            {step > 1 && (
              <AppButton
                type="button"
                onClick={() => setStep((prev) => (prev === 3 ? 2 : 1))}
                className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px]"
              >
                Back
              </AppButton>
            )}
            <AppButton
              type="button"
              onClick={handleClose}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px]"
            >
              Cancel
            </AppButton>
            {step < 3 ? (
              <AppButton
                type="button"
                onClick={goNext}
                disabled={
                  (step === 1 && mappingErrors.length > 0) ||
                  (step === 2 &&
                    (!spreadsheet ||
                      isParsing ||
                      (headerMatch?.missing.length ?? 1) > 0))
                }
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors text-[15px]"
              >
                Continue
              </AppButton>
            ) : (
              <AppButton
                type="button"
                onClick={() => void handleSubmit()}
                disabled={
                  validRows.length === 0 ||
                  (!skipInvalidRows && invalidRows.length > 0)
                }
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors text-[15px] disabled:opacity-50"
              >
                {`Upload ${skipInvalidRows ? validRows.length : mappedRows.length} ${entityLabel}`}
              </AppButton>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default BulkUploadModal;
